import React from 'react';
import { MoreVertical } from 'lucide-react';
import { BookingListItem, BookingStatus } from '../../model/professional/booking-model';
import { t } from 'i18next';

// Type pour adapter le statut de l'API à l'UI
type UIStatus = BookingStatus | 'canceled';

interface StatusInfo {
  icon: React.ReactNode;
  color: string;
  label: string;
}

interface ReservationTableProps {
  reservations: BookingListItem[];
  selectedReservations: string[];
  onToggleReservation: (id: string) => void;
  onShowDetails: (id: string) => void;
  getStatusInfo: (status: UIStatus | BookingStatus) => StatusInfo;
  formatDate: (dateString: string) => string;
  formatTime: (dateString: string) => string;
}

const ReservationTable: React.FC<ReservationTableProps> = ({
  reservations,
  selectedReservations,
  onToggleReservation,
  onShowDetails,
  getStatusInfo,
  formatDate,
  formatTime
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead className="text-xs uppercase bg-gray-50 dark:bg-gray-700 sticky top-0">
          <tr>
            <th className="px-4 py-3 font-medium w-4">
             {/*  <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-gray-600"
                checked={selectedReservations.length === reservations.length && reservations.length > 0}
                onChange={(e) => onToggleAll(e.target.checked)}
              /> */}
            </th>
            <th className="px-4 py-3 font-medium">{t('reservations.date', 'Date')}</th>
            <th className="px-4 py-3 font-medium">{t('reservations.time', 'Heure')}</th>
            <th className="px-4 py-3 font-medium">{t('reservations.clientName', 'Client')}</th>
            <th className="px-4 py-3 font-medium">{t('reservations.service', 'Service')}</th>
            <th className="px-4 py-3 font-medium">{t('reservations.duration', 'Durée')}</th>
            <th className="px-4 py-3 font-medium">{t('reservations.price', 'Prix')}</th>
            <th className="px-4 py-3 font-medium">{t('reservations.status', 'Statut')}</th>
            <th className="px-4 py-3 font-medium text-right">{t('reservations.actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {reservations.map((reservation) => {
            // Adapter le statut pour l'UI
            let uiStatus: UIStatus | BookingStatus = reservation.status;
            if (reservation.status === 'cancelled') {
              uiStatus = 'canceled' as UIStatus;
            }
            const statusInfo = getStatusInfo(uiStatus);
            
            return (
              <tr 
                key={reservation.id} 
                className={`hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer ${
                  reservation.status === 'cancelled' || reservation.status === 'completed'? 'opacity-60' : ''
                }`}
                onClick={() => onShowDetails(reservation.id)}
              >
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  {/* <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-gray-600"
                    checked={selectedReservations.includes(reservation.id)}
                    onChange={() => onToggleReservation(reservation.id)}
                  /> */}
                </td>
                <td className="px-4 py-3">{reservation.startTime ? formatDate(reservation.startTime) : (reservation.createdAt ? formatDate(reservation.createdAt) : t('reservations.dateUnknown', 'Date inconnue'))}</td>
                <td className="px-4 py-3">
                  {reservation.startTime ? (
                    `${formatTime(reservation.startTime)} - ${formatTime(reservation.endTime)}`
                  ) : (
                    <span className="text-gray-500 italic">{t('reservations.timeUnknown', 'Heure inconnue')}</span>
                  )}
                </td>
                <td className="px-4 py-3 font-medium">{`${reservation.clientFirstName} ${reservation.clientLastName}`}</td>
                <td className="px-4 py-3">
                  {reservation.services && reservation.services.length > 0
                    ? reservation.services.map(s => s.serviceName).join(', ')
                    : reservation.serviceName}
                </td>
                <td className="px-4 py-3">
                  {reservation.totalDuration
                    ? `${reservation.totalDuration} min`
                    : reservation.serviceDuration
                      ? `${reservation.serviceDuration} min`
                      : '-'}
                </td>
                <td className="px-4 py-3">{reservation.totalPrice}€</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                    <span className="mr-1">{statusInfo.icon}</span>
                    {statusInfo.label}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button 
                    className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowDetails(reservation.id);
                    }}
                  >
                    <MoreVertical size={16} />
                  </button>
                </td>
              </tr>
            );
          })}
          {reservations.length === 0 && (
            <tr>
              <td colSpan={9} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                {t('reservations.noReservationsFound', 'No reservations found')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ReservationTable;
